/**
 * Enhanced Drawer Toggle Action
 * Supports show/hide/toggle, left/right position, custom width, and selector targeting.
 * Usage: window.dispatchEvent(new CustomEvent('arc:action:drawerToggle', { detail: { action: 'show', selector: '#details-drawer', position: 'right', width: '400px' } }))
 */

export const runDrawerToggle = async (data = {}) => {
  try {
    const {
      action = 'toggle', // 'show', 'hide', 'toggle'
      selector,
      eventId,
      targetEventId,
      position = 'right', // 'left' or 'right'
      width = '600px',
      message = 'Hello from the event system!',
      type = 'info',
      duration = 3000
    } = data;

    // Use targetEventId or eventId as selector if provided, otherwise default to #details-drawer
    const finalSelector = selector || (targetEventId ? `#${targetEventId}` : (eventId ? `#${eventId}` : '#details-drawer'));

    // Debounce to prevent double execution
    const now = Date.now();
    if (runToggleDrawer._lastCall && (now - runToggleDrawer._lastCall) < 300) {
      return { success: true, message: 'Debounced duplicate call' };
    }
    runToggleDrawer._lastCall = now;

    // Validate action
    const validActions = ['show', 'hide', 'toggle'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    // Find drawer element(s)
    const drawers = document.querySelectorAll(finalSelector);
    if (!drawers.length) {
      throw new Error(`No drawer found for selector: ${finalSelector}`);
    }

    drawers.forEach((drawer) => {
      // Set base styles
      drawer.style.position = 'fixed';
      drawer.style.top = '0';
      drawer.style.height = '100dvh';
      drawer.style.maxWidth = width;
      drawer.style.boxShadow = '0 0 24px rgba(0,0,0,0.15)';
      drawer.style.zIndex = '10001';
      drawer.style.transition = 'transform 0.4s cubic-bezier(.4,0,.2,1)';
      drawer.style.display = 'block';
      drawer.style.opacity = 1;

      // Set position
      if (position === 'left') {
        drawer.style.left = '0';
        drawer.style.right = '';
        drawer.style.transform = 'translateX(-100%)';
      } else {
        drawer.style.right = '0';
        drawer.style.left = '';
        drawer.style.transform = 'translateX(100%)';
      }

      // Helper: is open? (read current DOM state)
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';

      // Action logic with requestAnimationFrame and forced reflow
      const doShow = () => {
        // Prevent opening if already animating or open
        if (drawer.dataset.isAnimating === 'true' || drawer.getAttribute('aria-hidden') === 'false') {
          if (window.enableDebugMode) { console.log('Drawer already open or animating, skipping show'); }
          return;
        }
        
        // Set animation flag to prevent immediate closure
        drawer.dataset.isAnimating = 'true';
        
        // Create backdrop
        const backdropId = `drawer-backdrop-${Date.now()}`;
        let backdrop = document.getElementById(backdropId);
        
        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.id = backdropId;
          backdrop.style.position = 'fixed';
          backdrop.style.top = '0';
          backdrop.style.left = '0';
          backdrop.style.width = '100vw';
          backdrop.style.height = '100vh';
          backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          backdrop.style.zIndex = '10000'; // One less than drawer
          backdrop.style.opacity = '0';
          backdrop.style.transition = 'opacity 0.4s cubic-bezier(.4,0,.2,1)';
          backdrop.style.cursor = 'pointer';
          
          // Add click handler with animation check
          const handleBackdropClick = (e) => {
            // Ignore clicks during animation
            if (drawer.dataset.isAnimating === 'true') {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            doHide();
          };
          
          backdrop.addEventListener('click', handleBackdropClick);
          
          // Store backdrop reference and handler on drawer for cleanup
          drawer.dataset.backdropId = backdropId;
          drawer._backdropHandler = handleBackdropClick;
          
          document.body.appendChild(backdrop);
        }
        
        // Start off-screen
        if (position === 'left') {
          drawer.style.transform = 'translateX(-100%)';
        } else {
          drawer.style.transform = 'translateX(100%)';
        }
        drawer.style.display = 'block';
        // Set open state
        drawer.setAttribute('aria-hidden', 'false');
        drawer.setAttribute('tabindex', '0');
        // Force reflow to ensure transition
        void drawer.offsetWidth;
        
        // Animate backdrop and drawer in
        requestAnimationFrame(() => {
          backdrop.style.opacity = '1';
          drawer.style.transform = 'translateX(0)';
        });
        
        setTimeout(() => {
          // Clear animation flag to allow backdrop clicks
          drawer.dataset.isAnimating = 'false';
          drawer.focus && drawer.focus();
        }, 400);
      };
      
      const doHide = () => {
        // Prevent closing if already animating or closed
        if (drawer.dataset.isAnimating === 'true' || drawer.getAttribute('aria-hidden') === 'true') {
          if (window.enableDebugMode) { console.log('Drawer already closed or animating, skipping hide'); }
          return;
        }
        
        // Set animation flag during closing
        drawer.dataset.isAnimating = 'true';
        
        // Remove backdrop
        const backdropId = drawer.dataset.backdropId;
        const backdrop = document.getElementById(backdropId);
        
        // Animate out
        requestAnimationFrame(() => {
          if (backdrop) {
            backdrop.style.opacity = '0';
          }
          if (position === 'left') {
            drawer.style.transform = 'translateX(-100%)';
          } else {
            drawer.style.transform = 'translateX(100%)';
          }
        });
        
        setTimeout(() => {
          // Remove focus before setting aria-hidden to avoid accessibility warning
          if (document.activeElement && drawer.contains(document.activeElement)) {
            document.activeElement.blur();
          }
          
          // Set closed state
          drawer.setAttribute('aria-hidden', 'true');
          drawer.setAttribute('tabindex', '-1');
          
          // Remove backdrop from DOM
          if (backdrop && drawer._backdropHandler) {
            backdrop.removeEventListener('click', drawer._backdropHandler);
            backdrop.remove();
          }
          
          // Clean up backdrop reference and handler
          delete drawer.dataset.backdropId;
          delete drawer._backdropHandler;
          delete drawer.dataset.isAnimating;
        }, 400);
      };

      if (action === 'show') {
        doShow();
      } else if (action === 'hide') {
        doHide();
      } else if (action === 'toggle') {
        isOpen ? doHide() : doShow();
      }
    });

    return {
      success: true,
      message: `Drawer ${action} executed` + (drawers.length > 1 ? ` (${drawers.length} elements)` : ''),
      data: { action, selector: finalSelector, position, width, message, type, duration }
    };
  } catch (error) {
    console.error('Error toggling drawer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export default runDrawerToggle;
